/**
 * Animates the key-features section when it scrolls into view:
 *   1. The center line grows from top to bottom
 *   2. Feature items appear one by one (opacity + translateY)
 *   3. The currently appearing item has a filled shield icon;
 *      all previously revealed items switch to outlined
 *   4. Final state: all items visible, all shields outlined
 *
 * Animations fire only once (IntersectionObserver unobserves after trigger).
 * Respects prefers-reduced-motion: if set, all items are shown immediately.
 */
(function () {
  'use strict';

  /** Delay (ms) before the line starts growing after section enters viewport */
  const LINE_START_DELAY = 150;

  /** Duration (ms) the line animation takes (matches CSS transition) */
  const LINE_DURATION = 1000;

  /** Delay (ms) between each item reveal */
  const ITEM_STAGGER = 400;

  /** Delay (ms) after line starts before first item appears */
  const FIRST_ITEM_DELAY = 350;

  /**
   * Initialise animation for a single .key-features section.
   * @param {HTMLElement} section – the .key-features element with [data-key-features-animate]
   */
  function initKeyFeaturesAnimation(section) {
    const timeline = section.querySelector('.key-features__timeline');
    if (!timeline) return;

    const items = Array.from(timeline.querySelectorAll('.key-features__item'));
    if (items.length === 0) return;

    // Collect node elements for shield icon state toggling
    const nodes = items.map((item) => item.querySelector('.key-features__node'));

    // Add the animated modifier class – CSS uses this to set initial hidden states
    section.classList.add('key-features--animated');

    // If user prefers reduced motion, show everything immediately and bail out
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      section.classList.add('key-features--line-visible');
      items.forEach((item) => item.classList.add('key-features__item--visible'));
      return;
    }

    let hasAnimated = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            hasAnimated = true;
            observer.unobserve(section);
            runAnimation(section, items, nodes);
          }
        });
      },
      {
        // Trigger when at least 15% of the timeline is visible, offset 50px from bottom
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.15,
      }
    );

    observer.observe(section);
  }

  /**
   * Run the full animation sequence.
   */
  function runAnimation(section, items, nodes) {
    // Step 1: Start the line growth
    setTimeout(() => {
      section.classList.add('key-features--line-visible');
    }, LINE_START_DELAY);

    // Step 2 & 3: Reveal items one by one
    items.forEach((item, index) => {
      const delay = LINE_START_DELAY + FIRST_ITEM_DELAY + index * ITEM_STAGGER;

      setTimeout(() => {
        // Remove active state from previous node
        if (index > 0 && nodes[index - 1]) {
          nodes[index - 1].classList.remove('key-features__node--active');
        }

        // Set current node as active (filled shield)
        if (nodes[index]) {
          nodes[index].classList.add('key-features__node--active');
        }

        // Reveal the item
        item.classList.add('key-features__item--visible');

        // If this is the last item, remove active state after its transition completes
        if (index === items.length - 1) {
          setTimeout(() => {
            if (nodes[index]) {
              nodes[index].classList.remove('key-features__node--active');
            }
          }, 600); // slightly after the item's CSS transition (400ms) finishes
        }
      }, delay);
    });
  }

  /**
   * Initialise all key-features sections on the page.
   */
  function init() {
    const sections = document.querySelectorAll('[data-key-features-animate]');
    sections.forEach(initKeyFeaturesAnimation);
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-initialise when sections are added / reloaded
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-key-features-animate]');
      if (section) {
        // In design mode, show everything immediately (no animation)
        section.classList.add('key-features--animated');
        section.classList.add('key-features--line-visible');
        const items = section.querySelectorAll('.key-features__item');
        items.forEach((item) => item.classList.add('key-features__item--visible'));
      }
    });
  }
})();
